import type { SkillBranch, Vector2 } from '@sorrem/shared';
import type { SkillTreeModel } from './skill-tree-model';
import {
    SKILL_TREE_DIAL_SIZE,
    SKILL_TREE_GROUP_SIZE,
    SKILL_TREE_NODE_SIZE,
    SKILL_TREE_SIGIL_POSITION,
    SKILL_TREE_SIGIL_SIZE,
} from './skill-tree-layout';
import { toGroupLinkKey, toSigilLinkKey, toSkillLinkKey } from './skill-tree-ids';

/**
 * Where the skill tree's links run, in tree units: the SVG links draw along
 * these lines, and the effects layer's pulses flow along them.
 */

/** One connector, between two circles (sigil, dial, or node) of the given radii. */
export interface LinkSpec {
    readonly key: string;
    readonly branch: SkillBranch;
    readonly from: Vector2;
    readonly fromRadius: number;
    readonly to: Vector2;
    readonly toRadius: number;
    /** 0 for an untrained link, 1 for a mastered skill or the sigil's links: thicker and brighter. */
    readonly strength: number;
    /** Alternates which side Essence wisps bow to. */
    readonly index: number;
    readonly isFaint: boolean;
    /** The skill it leads to is equipment-gated and off: Martial links turn dashed. */
    readonly isInactive: boolean;
}

function subtract(a: Vector2, b: Vector2): Vector2 {
    return { x: a.x - b.x, y: a.y - b.y };
}

/** A Martial link runs straight out of its hub at least this far past the hub's edge before it turns 45 degrees. */
export const MARTIAL_LINK_MIN_LEAD = 40;

/**
 * A route from `from` to `to` using only vertical, horizontal, and 45 degrees
 * runs: straight out of the hub along the dominant axis, then the diagonal
 * into the target. Links leaving one dial share a trunk and branch off it
 * like a tree, instead of crossing each other.
 *
 * The straight run is at least `minLead` long, so a link never leaves its
 * hub at 45 degrees. When the target lies too close to a diagonal for that, the
 * diagonal ends short of it and a last straight leg, along the other axis,
 * reaches it; kept no longer than `minLead`, it stays hidden under the
 * target when the lead is no longer than the target's radius.
 */
export function resolveOctilinearRoute(from: Vector2, to: Vector2, minLead = 0): Vector2[] {
    const { x: dx, y: dy } = subtract(to, from);
    const major = Math.max(Math.abs(dx), Math.abs(dy));
    const minor = Math.min(Math.abs(dx), Math.abs(dy));
    const majorIsX = Math.abs(dx) >= Math.abs(dy);
    const axis = majorIsX ? { x: Math.sign(dx), y: 0 } : { x: 0, y: Math.sign(dy) };
    const lead = Math.min(major, Math.max(major - minor, minLead));
    const bend = { x: from.x + axis.x * lead, y: from.y + axis.y * lead };
    // The diagonal covers what is left along the major axis, and as much of the minor one.
    const diagonal = major - lead;
    const corner = {
        x: bend.x + (majorIsX ? axis.x * diagonal : Math.sign(dx) * diagonal),
        y: bend.y + (majorIsX ? Math.sign(dy) * diagonal : axis.y * diagonal),
    };
    return [from, bend, corner, to].filter((point, index, all) => index === 0 || Math.hypot(point.x - all[index - 1]!.x, point.y - all[index - 1]!.y) > 0.01);
}

/** A Martial link's route between the edges of its two circles: straight out of its hub, then 45 degrees into its target. */
export function resolveMartialRoute(link: LinkSpec): Vector2[] {
    return trimPolyline(resolveOctilinearRoute(link.from, link.to, link.fromRadius + MARTIAL_LINK_MIN_LEAD), link.fromRadius, link.toRadius);
}

/** Cuts `start` units off the front of a polyline and `end` off the back, so it meets each circle's edge. */
export function trimPolyline(points: readonly Vector2[], start: number, end: number): Vector2[] {
    const trimFront = (line: readonly Vector2[], distance: number): Vector2[] => {
        let remaining = distance;
        for (let index = 1; index < line.length; index += 1) {
            const from = line[index - 1]!;
            const to = line[index]!;
            const length = Math.hypot(to.x - from.x, to.y - from.y);
            if (remaining < length) {
                const t = remaining / length;
                return [{ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t }, ...line.slice(index)];
            }
            remaining -= length;
        }
        return [line[line.length - 1]!];
    };
    return trimFront(trimFront(points, start).reverse(), end).reverse();
}

/** Points along a quadratic curve from `from` to `to`, bowed sideways by `bow` (a fraction of its length; the sign picks the side). */
export function listCurvePoints(from: Vector2, to: Vector2, bow: number, samples = 48): Vector2[] {
    const { x: dx, y: dy } = subtract(to, from);
    const control = { x: (from.x + to.x) / 2 - dy * bow, y: (from.y + to.y) / 2 + dx * bow };
    return Array.from({ length: samples + 1 }, (_, index) => {
        const t = index / samples;
        const u = 1 - t;
        return {
            x: u * u * from.x + 2 * u * t * control.x + t * t * to.x,
            y: u * u * from.y + 2 * u * t * control.y + t * t * to.y,
        };
    });
}

/** Moves a link's ends from the circle centers to their edges, along the straight line between them. */
export function resolveEdgeEnds(link: LinkSpec): { from: Vector2; to: Vector2 } {
    const { x: dx, y: dy } = subtract(link.to, link.from);
    const length = Math.hypot(dx, dy) || 1;
    return {
        from: { x: link.from.x + (dx / length) * link.fromRadius, y: link.from.y + (dy / length) * link.fromRadius },
        to: { x: link.to.x - (dx / length) * link.toRadius, y: link.to.y - (dy / length) * link.toRadius },
    };
}

/** An Essence link's flowing curve, between the edges of its two circles. */
export function resolveEssencePath(link: LinkSpec): { points: Vector2[]; length: number } {
    const { from, to } = resolveEdgeEnds(link);
    return { points: listCurvePoints(from, to, link.index % 2 === 0 ? 0.14 : -0.14, 64), length: Math.hypot(to.x - from.x, to.y - from.y) };
}

/** A Crafting link's slack chain curve, between the edges of its two circles; an unawakened (faint) one sags more. */
export function resolveCraftingPath(link: LinkSpec): Vector2[] {
    const { from, to } = resolveEdgeEnds(link);
    return listCurvePoints(from, to, link.isFaint ? 0.1 : 0.06, 64);
}

/** The line each link follows, parent to child, for its intro reveal. */
const LINK_CENTERLINES: Record<SkillBranch, (link: LinkSpec) => Vector2[]> = {
    martial: resolveMartialRoute,
    essence: (link) => resolveEssencePath(link).points,
    crafting: resolveCraftingPath,
};

export function resolveLinkCenterline(link: LinkSpec): Vector2[] {
    return LINK_CENTERLINES[link.branch](link);
}

/** Every link on the tree: sigil to each dial, dial to each grouping root, root to each skill. */
export function buildSkillTreeLinks(model: SkillTreeModel): LinkSpec[] {
    return [
        ...model.dials.map((dial, index): LinkSpec => ({
            key: toSigilLinkKey(dial.placement.branch),
            branch: dial.placement.branch,
            from: SKILL_TREE_SIGIL_POSITION,
            fromRadius: SKILL_TREE_SIGIL_SIZE / 2,
            to: dial.placement.position,
            toRadius: SKILL_TREE_DIAL_SIZE / 2,
            strength: 1,
            index,
            isFaint: dial.isUnawakened,
            isInactive: false,
        })),
        // Dial to each grouping root, worn as deep as the best-trained skill under it.
        ...model.groups.flatMap((group, index): LinkSpec[] => {
            const dial = model.dials.find((candidate) => candidate.placement.branch === group.placement.branch);
            if (!dial) return [];
            const strength = group.isUnawakened ? 0 : Math.max(0, ...group.nodes.map((node) => node.step / node.maxStep));
            return [{
                key: toGroupLinkKey(group.placement.id),
                branch: group.placement.branch,
                from: dial.placement.position,
                fromRadius: SKILL_TREE_DIAL_SIZE / 2,
                to: group.placement.position,
                toRadius: SKILL_TREE_GROUP_SIZE / 2,
                strength,
                index,
                isFaint: group.isUnawakened,
                isInactive: false,
            }];
        }),
        // Grouping root to each of its skills.
        ...model.groups.flatMap((group) => group.nodes.map((node, index): LinkSpec => ({
            key: toSkillLinkKey(node.placement.id),
            branch: node.placement.branch,
            from: group.placement.position,
            fromRadius: SKILL_TREE_GROUP_SIZE / 2,
            to: node.placement.position,
            toRadius: SKILL_TREE_NODE_SIZE / 2,
            strength: node.isUnawakened ? 0 : node.step / node.maxStep,
            index,
            isFaint: node.isUnawakened,
            isInactive: node.isInactive,
        }))),
    ];
}
